import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const PurchaseOrderContext = createContext();

export const PurchaseOrderProvider = ({ children }) => {
  const [purchaseOrders, setPurchaseOrders] = useState({
    data: [],
    meta: null,
    error: null,
    loading: false,
    summary: null,
  });

  const [statistics, setStatistics] = useState({
    data: null,
    error: null,
    loading: false,
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    currentPage: 1,
    totalPages: 1,
    status: "all", // all, pending, approved, received, cancelled
    date_from: "",
    date_to: "",
  });

  const { endpoint, queryString } = (() => {
    const baseQuery = {
      search: filterOptions.search || "",
      page: filterOptions.currentPage,
      limit: 10,
    };

    // Only include status if it's not "all"
    if (filterOptions.status && filterOptions.status !== "all") {
      baseQuery.status = filterOptions.status;
    }

    // Include date range if provided
    if (filterOptions.date_from) {
      baseQuery.date_from = filterOptions.date_from;
    }
    if (filterOptions.date_to) {
      baseQuery.date_to = filterOptions.date_to;
    }

    return {
      endpoint: "/purchase-orders",
      queryString: buildQueryString(baseQuery),
    };
  })();

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterOptions((prevState) => ({
        ...prevState,
        search: e.target.value,
      }));
    }
  };

  const handleStatusFilter = (status) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      status: status,
    }));
  };

  const handleDateFromFilter = (dateFrom) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      date_from: dateFrom,
    }));
  };

  const handleDateToFilter = (dateTo) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      date_to: dateTo,
    }));
  };

  const fetchPurchaseOrders = useCallback(async () => {
    setPurchaseOrders((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest(`${endpoint}${queryString}`);
      console.log("Purchase Orders API Response:", response);

      if (response.success) {
        setPurchaseOrders({
          data: response.data,
          meta: response.meta || response.pagination,
          error: null,
          loading: false,
          summary: response.summary || null,
        });

        setFilterOptions((prev) => ({
          ...prev,
          totalPages: response.meta?.last_page || response.pagination?.last_page,
        }));
      } else {
        throw new Error(response.message || "Failed to fetch purchase orders");
      }
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      setPurchaseOrders((prev) => ({
        ...prev,
        error: error.message || "Failed to fetch purchase orders",
        loading: false,
      }));
    }
  }, [endpoint, queryString]);

  const fetchStatistics = useCallback(async () => {
    setStatistics((prevState) => ({
      ...prevState,
      loading: true,
    }));

    try {
      const response = await getRequest("/purchase-orders/statistics");
      console.log("Purchase Orders Statistics API Response:", response);

      if (response.success) {
        setStatistics({
          data: response.data,
          error: null,
          loading: false,
        });
      } else {
        throw new Error(response.message || "Failed to fetch purchase orders statistics");
      }
    } catch (error) {
      console.error("Error fetching purchase orders statistics:", error);
      setStatistics((prev) => ({
        ...prev,
        error: error.message || "Failed to fetch purchase orders statistics",
        loading: false,
      }));
    }
  }, []);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [fetchPurchaseOrders]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  const handlePageChange = (page) => {
    setFilterOptions((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const totalPages = purchaseOrders.meta?.last_page || 1;

  const Pagination = usePaginationWithComponent({
    totalPages: totalPages,
    currentPage: filterOptions.currentPage,
    onPageChange: handlePageChange,
    visiblePages: 5,
  });

  return (
    <PurchaseOrderContext.Provider
      value={{
        purchaseOrders,
        statistics,
        filterOptions,
        handleSearch,
        handleStatusFilter,
        handleDateFromFilter,
        handleDateToFilter,
        refetch: fetchPurchaseOrders,
        refetchStatistics: fetchStatistics,
        Pagination,
      }}
    >
      {children}
    </PurchaseOrderContext.Provider>
  );
};

export const usePurchaseOrder = () => {
  const context = useContext(PurchaseOrderContext);
  if (!context) {
    throw new Error("usePurchaseOrder must be used within PurchaseOrderProvider");
  }
  return context;
};
